import { useAtomValue } from 'jotai';
import { WellboreMapData } from 'types/api-responses';
import { Line, Point } from 'components/map/well';
import { LINE_STYLES } from 'components/map/map-styles';
import { MapUserConfigAtom } from 'components/map/types';
import wellStyle from 'components/map/well.module.scss';

interface WellMapLayerProps {
  id: string;
  wellbore: WellboreMapData;
  mapConfigAtom: MapUserConfigAtom;
  /** Overrides every line's color (e.g. a pre-drill color-by rule). */
  colorOverride?: string;
}

// Component to render a single well (producing or sidetrack) on the map
export const WellMapLayer = ({
  id,
  wellbore,
  mapConfigAtom,
  colorOverride,
}: WellMapLayerProps) => {
  const { stick, survey, bottomHole, surfaceHole, type } = wellbore;
  const mapConfig = useAtomValue(mapConfigAtom);
  const wellLayers = mapConfig.wells.options;
  const isProducing = type === 'producing';

  // Tint a base line style with the color-by override, if one is set.
  const tint = (base: Record<string, unknown>) => {
    if (!colorOverride) return base;
    return { ...base, 'line-color': colorOverride };
  };

  // For sidetracks, only render survey
  if (!isProducing) {
    return survey ? (
      <Line
        id={`${id}-survey`}
        geojson={survey}
        style={tint(LINE_STYLES.RED_SOLID)}
      />
    ) : null;
  }

  // For producing wellbores, use same logic as Well component with mapConfig
  return (
    <>
      {stick && wellLayers.stick && !survey && (
        <Line
          id={`${id}-stick`}
          geojson={stick}
          style={tint(LINE_STYLES.BLUE_DOTTED)}
        />
      )}
      {survey && wellLayers.survey && (
        <Line
          id={`${id}-survey`}
          geojson={survey}
          style={tint(LINE_STYLES.BLACK_SOLID)}
        />
      )}
      {bottomHole && wellLayers.bottomHole && (
        <Point geojson={bottomHole} className={wellStyle.bottomHole} />
      )}
      {surfaceHole && wellLayers.surfaceHole && (
        <Point geojson={surfaceHole} className={wellStyle.surfaceHole} />
      )}
    </>
  );
};
